import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Megaphone, Palette, User, Code,
  Handshake, EyeOff, ArrowUpRight, ChevronRight,
  Server, Shield, Wrench, ShoppingCart, Database
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/JsonLd";

const audiences = [
  { icon: Megaphone, title: "Agenții de marketing", text: "Pentru echipe care vor să se concentreze pe strategie, campanii și rezultate, fără să transforme mentenanța WordPress într-o zonă consumatoare de timp." },
  { icon: Palette, title: "Studiouri de design și branding", text: "Pentru echipe care livrează site-uri bune, dar nu vor ca suportul recurent și micile probleme tehnice să le blocheze fluxul de lucru." },
  { icon: User, title: "Freelanceri cu mai mulți clienți", text: "Pentru profesioniști care au nevoie de un partener tehnic stabil pentru mentenanță, intervenții și hosting, fără să piardă controlul relației comerciale." },
  { icon: Code, title: "Companii IT și software", text: "Pentru firme care au nevoie de un layer tehnic dedicat WordPress și WooCommerce, fără să țină intern o echipă separată pentru suport recurent." },
];

const problems = [
  "Taskuri mici care întrerup proiectele importante",
  "Suport recurent greu de scalat intern",
  "Incidente tehnice care apar neplanificat",
  "Presiune din partea clienților pentru răspuns rapid",
  "WooCommerce și hosting care cer atenție constantă",
];

const services = [
  "Mentenanță WordPress recurentă",
  "Suport WordPress și intervenții tehnice",
  "WooCommerce support",
  "Update-uri gestionate proactiv",
  "Backup și restaurare",
  "Monitorizare uptime",
  "Verificări malware și securitate de bază",
  "Verificări funcționale pentru formulare și checkout",
  "Remedieri după incidente sau update-uri problematice",
  "Hosting managed și infrastructură",
  "Optimizări de performanță și recomandări tehnice",
  "Migrare hosting și reorganizare operațională",
];

const models = [
  { icon: EyeOff, title: "White-label", text: "Noi livrăm partea tehnică în spate, iar tu rămâi interfața principală pentru client. Este varianta potrivită atunci când vrei să păstrezi complet experiența de client sub brandul tău." },
  { icon: Handshake, title: "Co-managed", text: "Lucrăm împreună cu echipa ta, cu responsabilități clare de ambele părți. Tu păstrezi relația și controlul, iar noi preluăm zona de mentenanță, hosting sau suport specializat." },
  { icon: ArrowUpRight, title: "Escalare tehnică / suport specializat", text: "Tu gestionezi partea curentă, iar noi intervenim când apar incidente, probleme WooCommerce, nevoi de infrastructură sau situații mai complexe." },
];

const benefits = [
  { title: "Scazi volumul de taskuri operaționale", text: "Echipa ta nu mai este întreruptă constant de probleme mici, update-uri, incidente sau cereri recurente care consumă timp și atenție." },
  { title: "Scalezi mai ușor", text: "Poți administra mai multe site-uri și mai mulți clienți fără să crești imediat echipa internă." },
  { title: "Păstrezi controlul relației comerciale", text: "Tu decizi cât de vizibilă este colaborarea și cum este prezentată clientului. Noi nu intrăm în competiție cu tine." },
  { title: "Livrezi mai multă stabilitate", text: "Site-urile clienților tăi sunt gestionate mai clar, mai predictibil și cu mai puțină dependență de improvizație." },
  { title: "Ai acces la un partener tehnic real", text: "O combinație de mentenanță, infrastructură, performanță și intervenție tehnică matură, nu doar suport de suprafață." },
];

const whyHighlights = [
  "Experiență tehnică reală, susținută de Maze Digital",
  "Activi din 2017 în programare IT și soluții digitale",
  "Procese clare de lucru și onboarding",
  "Suport WordPress și WooCommerce orientat spre continuitate",
  "Infrastructură și performanță, nu doar update-uri",
];

const steps = [
  { num: "01", title: "Discuție inițială", text: "Înțelegem ce tip de clienți ai, câte site-uri administrezi și ce vrei să externalizezi." },
  { num: "02", title: "Stabilim modelul de colaborare", text: "Definim dacă lucrăm white-label, împreună cu echipa ta sau ca partener tehnic transparent." },
  { num: "03", title: "Audit tehnic și prioritizare", text: "Analizăm site-urile existente, identificăm riscurile și stabilim ce poate intra direct în proces." },
  { num: "04", title: "Preluare și operare", text: "Începem mentenanța, suportul și infrastructura conform modelului agreat, cu procese clare." },
];

const pricingOptions = [
  "Tarif per site",
  "Pachet lunar pentru un portofoliu de proiecte",
  "Suport punctual sau model de escalare",
  "Hosting și mentenanță incluse pentru grupuri de site-uri",
  "Model custom pentru volume mai mari sau nevoi speciale",
];

const faqData = [
  { q: "Lucrați white-label?", a: "Da. Putem colabora în spatele brandului tău, astfel încât tu să rămâi punctul principal de contact pentru client." },
  { q: "Putem păstra noi relația cu clientul final?", a: "Da. În majoritatea parteneriatelor, relația principală rămâne la partener, iar noi gestionăm partea tehnică." },
  { q: "Puteți prelua și hostingul?", a: "Da. În majoritatea cazurilor, preluăm și hostingul, pentru a putea administra corect platforma în mediul nostru și pentru a avea control asupra stabilității, backup-urilor, securității și intervențiilor tehnice." },
  { q: "Colaborați și cu freelanceri sau studiouri mici?", a: "Da. Nu lucrăm doar cu agenții mari. Putem construi parteneriate și pentru freelanceri sau echipe mici care administrează mai multe site-uri." },
  { q: "Puteți prelua doar WooCommerce sau doar problemele mai complexe?", a: "Da. Colaborarea poate fi recurentă, punctuală sau structurată ca suport de escalare pentru cazurile mai dificile." },
  { q: "Putem începe cu doar câteva site-uri?", a: "Da. Colaborarea poate începe gradual, cu un număr mic de proiecte, apoi poate fi extinsă pe măsură ce procesul se validează." },
  { q: "Ce se întâmplă dacă unele site-uri au deja probleme?", a: "Începem cu un audit tehnic și stabilim ce poate intra direct în mentenanță și ce are nevoie de remedieri inițiale." },
];

export default function B2BPage() {
  usePageMeta(
    "Externalizare Mentenanță WordPress pentru Agenții - Ordell WebCare",
    "Externalizează mentenanța WordPress, suportul WooCommerce și hostingul. Parteneriat white-label sau co-managed pentru agenții, studiouri și freelanceri."
  );

  return (
    <>
      <JsonLd page="b2b" />
      {/* HERO */}
      <section data-testid="b2b-hero" className="py-16 lg:py-28">
        <div className="section-container max-w-4xl">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#1A4A40] mb-4">Parteneriat B2B</p>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none font-bold text-[#1A1D20] mb-6">
            Externalizează mentenanța WordPress.<br />
            <span className="text-[#0F2942]">Păstrează relația cu clientul.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed mb-4 max-w-3xl">
            Ordell WebCare ajută agenții, studiouri și companii care administrează site-uri pentru clienții lor să externalizeze mentenanța, suportul, hostingul și partea tehnică recurentă - fără să își încarce echipa internă și fără să complice relația comercială.
          </p>
          <p className="text-sm text-[#4A5568] leading-relaxed mb-8 max-w-3xl">
            Lucrăm ca partener tehnic în spatele serviciilor tale, astfel încât tu să te poți concentra pe strategie, design, development, marketing și creștere.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <Link to="/contact" data-testid="b2b-cta-primary" className="bg-[#0F2942] text-white font-medium px-6 py-3 rounded-xl hover:bg-[#0F2942]/90 transition-all text-center flex items-center justify-center gap-2">
              Discută un parteneriat <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#cum-lucram" data-testid="b2b-cta-secondary" className="bg-white text-[#0F2942] border border-[#0F2942]/10 font-medium px-6 py-3 rounded-xl hover:bg-[#F0F5F4] transition-all text-center">
              Vezi cum lucrăm
            </a>
          </div>
          <p className="text-xs text-[#64748B]">
            White-label, colaborare directă sau model mixt - în funcție de cum vrei să îți structurezi relația cu clienții.
          </p>
        </div>
      </section>

      {/* PROBLEM */}
      <section data-testid="b2b-problem" className="py-20 lg:py-28 bg-[#F1F3F5]">
        <div className="section-container">
          <div className="max-w-3xl mb-10">
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-6">
              Nu toate companiile care livrează site-uri vor sau trebuie să ducă intern toată partea tehnică recurentă.
            </h2>
            <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed mb-4">
              Dacă administrezi site-uri pentru clienți, probabil cunoști deja partea invizibilă a muncii: update-uri, incidente, formulare care nu mai funcționează, pluginuri care creează conflicte, solicitări mici care se adună și magazine online care au nevoie de atenție constantă.
            </p>
            <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed">
              Când echipa ta este ocupată cu proiecte mai importante, mentenanța începe să consume timp, să creeze fricțiune și să apară exact când nu trebuie.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {problems.map((p, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-[#0F2942]/5">
                <ChevronRight className="w-4 h-4 text-[#0F2942]/40 mt-0.5 shrink-0" />
                <span className="text-sm text-[#1A1D20]/80">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section data-testid="b2b-solution" className="py-20 lg:py-28">
        <div className="section-container max-w-3xl">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-6">
            Noi preluăm partea tehnică și operațională. Tu rămâi owner pe relația cu clientul.
          </h2>
          <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed mb-4">
            Ordell WebCare funcționează ca partener tehnic pentru companiile care vor să externalizeze mentenanța WordPress, suportul WooCommerce, hostingul și problemele recurente. Putem lucra discret în spatele brandului tău, în colaborare cu echipa ta sau într-un model transparent.
          </p>
          <p className="text-sm text-[#4A5568] leading-relaxed">
            Scopul nu este să înlocuim relația ta cu clientul, ci să îți oferim o structură tehnică stabilă în spate, astfel încât livrarea să fie mai clară, mai predictibilă și mai ușor de scalat.
          </p>
        </div>
      </section>

      {/* AUDIENCE */}
      <section data-testid="b2b-audience" className="py-20 lg:py-28 bg-[#F1F3F5]">
        <div className="section-container">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-10">
            Potrivit pentru companii care administrează site-uri, dar nu vor să ducă intern toată bătaia de cap.
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {audiences.map((a, i) => (
              <div key={i} data-testid={`b2b-audience-${i}`} className="bg-white rounded-2xl p-8 border border-[#0F2942]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <div className="w-10 h-10 rounded-xl bg-[#F0F5F4] flex items-center justify-center mb-4">
                  <a.icon className="w-5 h-5 text-[#1A4A40]" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-medium text-[#1A1D20] mb-2">{a.title}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{a.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="b2b-services" className="py-20 lg:py-28">
        <div className="section-container">
          <div className="max-w-3xl mb-10">
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-4">
              Ce putem gestiona pentru tine și pentru clienții tăi
            </h2>
            <p className="text-[#4A5568] leading-relaxed">
              Putem prelua integral sau parțial partea tehnică recurentă, în funcție de modelul tău de business și de tipul de portofoliu pe care îl administrezi.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((s, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-[#F0F5F4]/50">
                <Check className="w-4 h-4 text-[#1A4A40] mt-0.5 shrink-0" />
                <span className="text-sm text-[#1A1D20]">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MODELS */}
      <section id="cum-lucram" data-testid="b2b-models" className="py-20 lg:py-28 bg-[#0A1118] text-white scroll-mt-20">
        <div className="section-container">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold mb-10">
            Colaborăm în modelul care se potrivește cel mai bine business-ului tău
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {models.map((m, i) => (
              <div key={i} className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                  <m.icon className="w-5 h-5 text-[#4A9E96]" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-medium mb-3">{m.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{m.text}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-white/60 mt-8">
            Nu impunem un model rigid. Structurăm colaborarea astfel încât să fie sustenabilă pentru echipa ta și confortabilă pentru clientul final.
          </p>
        </div>
      </section>

      {/* BENEFITS */}
      <section data-testid="b2b-benefits" className="py-20 lg:py-28">
        <div className="section-container">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-10">
            Mai puțină presiune pe echipa ta. Mai multă consistență pentru clienții tăi.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-[#0F2942]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <h3 className="font-['Outfit'] text-base font-medium text-[#1A1D20] mb-2">{b.title}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY ORDELL */}
      <section data-testid="b2b-why" className="py-20 lg:py-28 bg-[#F1F3F5]">
        <div className="section-container max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-['Outfit'] text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20] mb-4">
                Un partener tehnic serios, susținut de experiența Maze Digital
              </h2>
              <p className="text-[#4A5568] leading-relaxed">
                Ordell WebCare este un serviciu Maze Digital, companie activă din 2017 în zona de programare IT și soluții digitale. Partenerii noștri nu lucrează doar cu un furnizor de mentenanță, ci cu o echipă care înțelege dezvoltarea, infrastructura, WordPress, WooCommerce și impactul real al problemelor tehnice asupra business-ului.
              </p>
            </div>
            <div className="space-y-3">
              {whyHighlights.map((h, i) => (
                <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-[#0F2942]/10">
                  <Check className="w-4 h-4 text-[#1A4A40] mt-0.5 shrink-0" />
                  <span className="text-sm text-[#1A1D20]">{h}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INFRASTRUCTURE */}
      <section data-testid="b2b-infra" className="py-20 lg:py-28 bg-[#0A1118] text-white">
        <div className="section-container max-w-3xl mx-auto text-center">
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-6">
            <Server className="w-6 h-6 text-[#4A9E96]" />
          </div>
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold mb-6">
            Când proiectele au nevoie de mai mult, și infrastructura trebuie să fie la nivelul potrivit
          </h2>
          <p className="text-white/70 leading-relaxed mb-4">
            Pentru proiectele unde stabilitatea, traficul și continuitatea contează, putem prelua și partea de hosting sau infrastructură. În funcție de tipul site-urilor din portofoliul tău, putem recomanda și implementa un setup mai potrivit pentru WordPress și WooCommerce.
          </p>
          <p className="text-sm text-white/60 italic">
            Mai puține limitări tipice hostingului low-cost. Mai multă stabilitate pentru site-urile pe care le administrezi.
          </p>
        </div>
      </section>

      {/* PROCESS */}
      <section data-testid="b2b-process" className="py-20 lg:py-28">
        <div className="section-container">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-10 text-center">
            Proces simplu de onboarding
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {steps.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-[#0F2942]/10">
                <span className="font-['Outfit'] text-3xl font-bold text-[#0F2942]/10 block mb-3">{s.num}</span>
                <h3 className="font-['Outfit'] text-lg font-medium text-[#1A1D20] mb-2">{s.title}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section data-testid="b2b-pricing" className="py-20 lg:py-28 bg-[#F1F3F5]">
        <div className="section-container max-w-3xl">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20] mb-4">
            Parteneriatele B2B se configurează în funcție de volum, complexitate și modelul de lucru
          </h2>
          <p className="text-[#4A5568] leading-relaxed mb-8">
            Nu folosim o formulă rigidă pentru toate parteneriatele. În funcție de numărul de site-uri, tipul clienților și nivelul de implicare necesar, putem construi colaborarea pe bază de:
          </p>
          <div className="space-y-3 mb-8">
            {pricingOptions.map((p, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-[#0F2942]/10">
                <Check className="w-4 h-4 text-[#1A4A40] mt-0.5 shrink-0" />
                <span className="text-sm text-[#1A1D20]">{p}</span>
              </div>
            ))}
          </div>
          <Link to="/contact" data-testid="b2b-pricing-cta" className="inline-flex items-center gap-2 bg-[#0F2942] text-white font-medium px-6 py-3 rounded-xl hover:bg-[#0F2942]/90 transition-all">
            Solicită o discuție de parteneriat <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="b2b-faq" className="py-20 lg:py-28">
        <div className="section-container max-w-3xl">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20] mb-8">
            Întrebări frecvente despre parteneriate
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqData.map((item, i) => (
              <AccordionItem key={i} value={`b2b-faq-${i}`} className="bg-white rounded-xl border border-[#0F2942]/10 px-6 data-[state=open]:shadow-sm">
                <AccordionTrigger data-testid={`b2b-faq-${i}`} className="text-left text-[#1A1D20] font-medium hover:no-underline py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#4A5568] leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="b2b-final-cta" className="py-20 lg:py-28 bg-[#0A1118] text-white">
        <div className="section-container text-center max-w-3xl mx-auto">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold mb-4">
            Lasă partea tehnică recurentă în grija unei echipe care știe să o gestioneze corect
          </h2>
          <p className="text-white/70 leading-relaxed mb-8">
            Dacă administrezi site-uri pentru clienți și vrei un partener tehnic stabil, clar și ușor de integrat în procesul tău, hai să discutăm.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact" data-testid="b2b-final-cta-btn" className="inline-flex items-center justify-center gap-2 bg-white text-[#0F2942] font-medium px-8 py-3.5 rounded-xl hover:bg-white/90 transition-all">
              Discută un parteneriat <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="text-xs text-white/50 mt-4">
            Fără presiune. Fără jargon inutil. Doar o discuție clară despre cum putem lucra împreună.
          </p>
        </div>
      </section>
    </>
  );
}
